import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, TrendingDown } from "lucide-react";

type Row = { metric: string; value: number; rating: string; path: string; created_at: string };

// Google's Core Web Vitals thresholds (good / needs-improvement boundary, poor boundary)
const THRESHOLDS: Record<string, { good: number; poor: number; unit: string; label: string }> = {
  LCP: { good: 2500, poor: 4000, unit: "ms", label: "Largest Contentful Paint" },
  INP: { good: 200, poor: 500, unit: "ms", label: "Interaction to Next Paint" },
  CLS: { good: 0.1, poor: 0.25, unit: "", label: "Cumulative Layout Shift" },
  FCP: { good: 1800, poor: 3000, unit: "ms", label: "First Contentful Paint" },
  TTFB: { good: 800, poor: 1800, unit: "ms", label: "Time to First Byte" },
};

const ORDER = ["LCP", "INP", "CLS", "FCP", "TTFB"];

function percentile(values: number[], p: number) {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
}

function ratingOf(metric: string, value: number) {
  const t = THRESHOLDS[metric];
  if (!t) return "good";
  if (value <= t.good) return "good";
  if (value <= t.poor) return "needs-improvement";
  return "poor";
}

function format(metric: string, value: number) {
  const t = THRESHOLDS[metric];
  if (!t) return value.toFixed(2);
  if (metric === "CLS") return value.toFixed(3);
  return `${Math.round(value)} ${t.unit}`;
}

export default function WebVitalsPanel() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase
      .from("web_vitals" as never)
      .select("metric,value,rating,path,created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(5000);
    if (!error && data) setRows(data as unknown as Row[]);
    setLoading(false);
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

  const totals = rows.length;
  const summary = ORDER.map((m) => {
    const vals = rows.filter((r) => r.metric === m).map((r) => Number(r.value));
    const p75 = percentile(vals, 75);
    return { metric: m, count: vals.length, p75, rating: ratingOf(m, p75) };
  });

  // Alerts: any metric where p75 is poor, or where recent values degraded vs previous half-window
  const alerts: { metric: string; reason: string; severity: "high" | "warn" }[] = [];
  summary.forEach((s) => {
    if (s.count < 5) return;
    if (s.rating === "poor") {
      alerts.push({ metric: s.metric, severity: "high", reason: `p75 = ${format(s.metric, s.p75)} (poor)` });
    } else if (s.rating === "needs-improvement") {
      alerts.push({ metric: s.metric, severity: "warn", reason: `p75 = ${format(s.metric, s.p75)} (needs improvement)` });
    }
    // Trend check: compare last half vs first half
    const mRows = rows.filter((r) => r.metric === s.metric).map((r) => Number(r.value));
    if (mRows.length >= 20) {
      const half = Math.floor(mRows.length / 2);
      const recent = percentile(mRows.slice(0, half), 75); // most recent (DESC order)
      const older = percentile(mRows.slice(half), 75);
      if (older > 0 && recent > older * 1.25) {
        alerts.push({
          metric: s.metric,
          severity: "warn",
          reason: `regresja: ${format(s.metric, older)} → ${format(s.metric, recent)} (+${Math.round((recent / older - 1) * 100)}%)`,
        });
      }
    }
  });

  const worstPages = (() => {
    const lcp = rows.filter((r) => r.metric === "LCP");
    const byPath = new Map<string, number[]>();
    lcp.forEach((r) => {
      const arr = byPath.get(r.path) ?? [];
      arr.push(Number(r.value));
      byPath.set(r.path, arr);
    });
    return Array.from(byPath.entries())
      .map(([path, vals]) => ({ path, p75: percentile(vals, 75), count: vals.length }))
      .filter((x) => x.count >= 3)
      .sort((a, b) => b.p75 - a.p75)
      .slice(0, 5);
  })();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6" /> Core Web Vitals
          </h2>
          <p className="text-sm text-muted-foreground">
            Realne pomiary z przeglądarek użytkowników (RUM). Próbka: 25% sesji.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setDays(1)} className={days === 1 ? "bg-secondary" : ""}>24h</Button>
          <Button variant="outline" size="sm" onClick={() => setDays(7)} className={days === 7 ? "bg-secondary" : ""}>7 dni</Button>
          <Button variant="outline" size="sm" onClick={() => setDays(30)} className={days === 30 ? "bg-secondary" : ""}>30 dni</Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 ? (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <Alert key={i} variant={a.severity === "high" ? "destructive" : "default"}>
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>{a.metric} — {a.severity === "high" ? "krytyczny" : "ostrzeżenie"}</AlertTitle>
              <AlertDescription>{a.reason}</AlertDescription>
            </Alert>
          ))}
        </div>
      ) : totals > 0 ? (
        <Alert>
          <CheckCircle2 className="w-4 h-4" />
          <AlertTitle>Wszystko OK</AlertTitle>
          <AlertDescription>Brak metryk poza progami. Łącznie {totals} pomiarów.</AlertDescription>
        </Alert>
      ) : null}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {summary.map((s) => {
          const t = THRESHOLDS[s.metric];
          const color = s.rating === "good" ? "bg-green-500/10 border-green-500/30 text-green-700"
            : s.rating === "needs-improvement" ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-800"
            : "bg-red-500/10 border-red-500/30 text-red-700";
          return (
            <Card key={s.metric} className={s.count === 0 ? "opacity-60" : ""}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  {s.metric}
                  {s.count > 0 && (
                    <Badge className={color} variant="outline">{s.rating === "good" ? "dobry" : s.rating === "needs-improvement" ? "średni" : "słaby"}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {s.count === 0 ? "—" : format(s.metric, s.p75)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  p75 · {s.count} pom. · próg ≤ {s.metric === "CLS" ? t.good.toFixed(2) : `${t.good}${t.unit}`}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Worst pages by LCP */}
      {worstPages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="w-4 h-4" /> Najwolniejsze strony (LCP p75)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {worstPages.map((p) => {
                const r = ratingOf("LCP", p.p75);
                return (
                  <div key={p.path} className="flex items-center justify-between py-2 border-b last:border-0">
                    <code className="text-sm">{p.path}</code>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">{p.count} pom.</span>
                      <Badge variant={r === "poor" ? "destructive" : r === "needs-improvement" ? "secondary" : "outline"}>
                        {format("LCP", p.p75)}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {totals === 0 && !loading && (
        <p className="text-center text-muted-foreground py-8">
          Brak danych w wybranym okresie. Pomiary pojawią się po wizytach użytkowników.
        </p>
      )}
    </div>
  );
}
